import * as THREE from 'three'
import { GPUComputationRenderer } from 'three/addons/misc/GPUComputationRenderer.js'
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js'
import { params } from '../config/persistence.js'
import { createShapeGeometry } from './geometry.js'
import gpgpuParticlesShader from '../shaders/gpgpu/particles.glsl'
import gpgpuVelocityShader from '../shaders/gpgpu/velocity.glsl'

export const initCompute = (renderer, sampledPositions, particleCount) => {
    const compute = {}
    compute.size = Math.ceil(Math.sqrt(particleCount))
    compute.computation = new GPUComputationRenderer(compute.size, compute.size, renderer)

    const baseParticlesTexture = compute.computation.createTexture()

    for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3
        const i4 = i * 4
        baseParticlesTexture.image.data[i4 + 0] = sampledPositions[i3 + 0]
        baseParticlesTexture.image.data[i4 + 1] = sampledPositions[i3 + 1]
        baseParticlesTexture.image.data[i4 + 2] = sampledPositions[i3 + 2]
        baseParticlesTexture.image.data[i4 + 3] = Math.random()
    }

    let baseMorphTexture
    if (params.morphEnabled) {
        const morphGeometry = createShapeGeometry(params.morphTarget, params)
        const morphMesh = new THREE.Mesh(morphGeometry, new THREE.MeshBasicMaterial())
        const morphSampler = new MeshSurfaceSampler(morphMesh).build()
        const morphPositions = new Float32Array(particleCount * 3)
        const tempPosition = new THREE.Vector3()

        for (let i = 0; i < particleCount; i++) {
            morphSampler.sample(tempPosition)
            morphPositions[i * 3 + 0] = tempPosition.x
            morphPositions[i * 3 + 1] = tempPosition.y
            morphPositions[i * 3 + 2] = tempPosition.z
        }

        baseMorphTexture = compute.computation.createTexture()
        for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3
            const i4 = i * 4
            baseMorphTexture.image.data[i4 + 0] = morphPositions[i3 + 0]
            baseMorphTexture.image.data[i4 + 1] = morphPositions[i3 + 1]
            baseMorphTexture.image.data[i4 + 2] = morphPositions[i3 + 2]
            baseMorphTexture.image.data[i4 + 3] = 0
        }
    } else {
        baseMorphTexture = compute.computation.createTexture()
    }

    const baseVelocityTexture = compute.computation.createTexture()

    compute.particlesVariable = compute.computation.addVariable('uParticles', gpgpuParticlesShader, baseParticlesTexture)
    compute.velocityVariable = compute.computation.addVariable('uVelocity', gpgpuVelocityShader, baseVelocityTexture)

    compute.computation.setVariableDependencies(compute.particlesVariable, [compute.particlesVariable, compute.velocityVariable])
    compute.computation.setVariableDependencies(compute.velocityVariable, [compute.velocityVariable, compute.particlesVariable])

    // Particles uniforms
    const pU = compute.particlesVariable.material.uniforms
    pU.uTime = new THREE.Uniform(0)
    pU.uDeltaTime = new THREE.Uniform(0)
    pU.uBase = new THREE.Uniform(baseParticlesTexture)
    pU.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
    pU.uMouseRadius = new THREE.Uniform(params.mouseRadius)
    pU.uMouseAspect = new THREE.Uniform(params.mouseAspect)
    pU.uSuckAmount = new THREE.Uniform(0)
    pU.uMouseTrail = new THREE.Uniform(params.mouseTrail)
    pU.uDecayRate = new THREE.Uniform(params.respawnRate)
    pU.uBase2 = new THREE.Uniform(baseMorphTexture)
    pU.uMorphAmount = new THREE.Uniform(0)
    pU.uSuctionBarsEnabled = new THREE.Uniform(params.suctionBarsEnabled ? 1.0 : 0.0)
    pU.uSuctionBarStrength = new THREE.Uniform(params.suctionBarStrength)
    pU.uSuctionBarRadius = new THREE.Uniform(params.suctionBarRadius)
    pU.uSuctionKillFactor = new THREE.Uniform(params.suctionKillFactor ?? 0.4)
    pU.uSuctionBar0Pos = new THREE.Uniform(new THREE.Vector3(params.suctionBar0Pos.x, params.suctionBar0Pos.y, params.suctionBar0Pos.z))
    pU.uSuctionBar0Size = new THREE.Uniform(new THREE.Vector3(params.suctionBar0Size.x, params.suctionBar0Size.y, params.suctionBar0Size.z))
    pU.uSuctionBar1Pos = new THREE.Uniform(new THREE.Vector3(params.suctionBar1Pos.x, params.suctionBar1Pos.y, params.suctionBar1Pos.z))
    pU.uSuctionBar1Size = new THREE.Uniform(new THREE.Vector3(params.suctionBar1Size.x, params.suctionBar1Size.y, params.suctionBar1Size.z))
    pU.uCameraZ = new THREE.Uniform(18)
    pU.uFlatten = new THREE.Uniform(params.flatten2D ? 1.0 : 0.0)
    pU.uTextTipScale = new THREE.Uniform(params.textAvoidTip ?? 1.6)
    pU.uTextTipDown = new THREE.Uniform(params.textAvoidTipDown ?? 2.2)
    pU.uTextSolid = new THREE.Uniform(params.textAvoidSolid === false ? 0.0 : 1.0)
    pU.uSpawnNoiseFreq = new THREE.Uniform(params.spawnNoiseFreq ?? 0.3)
    pU.uSpawnNoiseSpeed = new THREE.Uniform(params.spawnNoiseSpeed ?? 0.4)
    pU.uSpawnNoiseAmp = new THREE.Uniform(params.spawnNoiseAmp ?? 0)
    pU.uSpawnNoiseGate = new THREE.Uniform(params.spawnNoiseGate ?? -1)
    pU.uSpawnBottomOnly = new THREE.Uniform(0)
    // Flusso discendente: la fascia di spawn offscreen sta sopra lo schermo
    pU.uSpawnBottomY = new THREE.Uniform(6.8)
    pU.uSpawnBottomSpread = new THREE.Uniform(16)
    pU.uSpawnEdgeBias = new THREE.Uniform(params.spawnEdgeBias ?? 1.0)
    pU.uSpawnEdgeAmount = new THREE.Uniform(0)
    pU.uSpawnEdgeFloor = new THREE.Uniform(params.spawnEdgeFloor ?? 0.0)
    pU.uPathKillY = new THREE.Uniform(99999)
    pU.uPathKinematic = new THREE.Uniform(params.pathKinematic ? 1.0 : 0.0)
    pU.uPathSpread = new THREE.Uniform(11)
    pU.uPathSpeed = new THREE.Uniform(params.pathDriftSpeed ?? 2.5)
    pU.uPathWaveFreq = new THREE.Uniform(params.pathWaveFreq ?? 0.35)
    pU.uPathWaveAmp = new THREE.Uniform(params.pathWaveAmp ?? 0.9)
    pU.uPathWaveSpeed = new THREE.Uniform(params.pathWaveSpeed ?? 0.6)
    pU.uTextBox0 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    pU.uTextBox0Strength = new THREE.Uniform(0)
    pU.uTextBox1 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    pU.uTextBox1Strength = new THREE.Uniform(0)
    pU.uTextCore0 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    pU.uTextCore1 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    pU.uTextTips0 = new THREE.Uniform(new THREE.Vector2(0, 0))
    pU.uTextTips1 = new THREE.Uniform(new THREE.Vector2(0, 0))
    pU.uTextDodge = new THREE.Uniform(params.textDodge ? 1.0 : 0.0)
    pU.uDomainHalfW = new THREE.Uniform(16)
    // Profilo di repulsione (curva di Bezier -> LUT). Default disattivo con una
    // texture 1x1: la home resta sui profili analitici, carbon lo attiva a runtime
    const profileDefaultTex = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType)
    profileDefaultTex.needsUpdate = true
    pU.uProfileEnabled = new THREE.Uniform(0)
    pU.uProfileTex = new THREE.Uniform(profileDefaultTex)
    pU.uProfileYMin = new THREE.Uniform(0)
    pU.uProfileYMax = new THREE.Uniform(1)

    // Velocity uniforms
    const vU = compute.velocityVariable.material.uniforms
    vU.uTime = new THREE.Uniform(0)
    vU.uDeltaTime = new THREE.Uniform(0)
    vU.uBase = new THREE.Uniform(baseParticlesTexture)
    vU.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
    vU.uMouseRadius = new THREE.Uniform(params.mouseRadius)
    vU.uMouseAspect = new THREE.Uniform(params.mouseAspect)
    vU.uSuckAmount = new THREE.Uniform(0)
    vU.uSuckStrength = new THREE.Uniform(params.suckStrength)
    vU.uMouseVelocity = new THREE.Uniform(new THREE.Vector3())
    vU.uWindStrength = new THREE.Uniform(params.windStrength)
    vU.uDamping = new THREE.Uniform(params.damping)
    vU.uTurbulenceStrength = new THREE.Uniform(params.turbulenceStrength)
    vU.uTurbulenceFrequency = new THREE.Uniform(params.turbulenceFrequency)
    vU.uFlowFieldFrequency = new THREE.Uniform(params.flowFrequency)
    vU.uFlowFieldStrength = new THREE.Uniform(params.flowStrength)
    vU.uFlowFieldInfluence = new THREE.Uniform(params.flowInfluence)
    vU.uBase2 = new THREE.Uniform(baseMorphTexture)
    vU.uMorphAmount = new THREE.Uniform(0)
    vU.uMorphStrength = new THREE.Uniform(params.morphEnabled ? params.morphStrength : 0)
    vU.uSuctionBarsEnabled = new THREE.Uniform(params.suctionBarsEnabled ? 1.0 : 0.0)
    vU.uSuctionBarStrength = new THREE.Uniform(params.suctionBarStrength)
    vU.uSuctionBarRadius = new THREE.Uniform(params.suctionBarRadius)
    vU.uSuctionBar0Pos = new THREE.Uniform(new THREE.Vector3(params.suctionBar0Pos.x, params.suctionBar0Pos.y, params.suctionBar0Pos.z))
    vU.uSuctionBar0Size = new THREE.Uniform(new THREE.Vector3(params.suctionBar0Size.x, params.suctionBar0Size.y, params.suctionBar0Size.z))
    vU.uSuctionBar1Pos = new THREE.Uniform(new THREE.Vector3(params.suctionBar1Pos.x, params.suctionBar1Pos.y, params.suctionBar1Pos.z))
    vU.uSuctionBar1Size = new THREE.Uniform(new THREE.Vector3(params.suctionBar1Size.x, params.suctionBar1Size.y, params.suctionBar1Size.z))
    vU.uUpdraftStrength = new THREE.Uniform(params.updraftStrength || 0)
    vU.uCameraZ = new THREE.Uniform(18)
    vU.uFlatten = new THREE.Uniform(params.flatten2D ? 1.0 : 0.0)
    vU.uTextAvoidRadius = new THREE.Uniform(params.textAvoidRadius || 2.0)
    vU.uTextTipScale = new THREE.Uniform(params.textAvoidTip ?? 1.6)
    vU.uTextTipDown = new THREE.Uniform(params.textAvoidTipDown ?? 2.2)
    vU.uTextTipAttract = new THREE.Uniform(params.textTipAttract ?? 0.08)
    vU.uTextTipReach = new THREE.Uniform(params.textTipAttractRadius ?? 9)
    vU.uTextSolid = new THREE.Uniform(params.textAvoidSolid === false ? 0.0 : 1.0)
    vU.uPathEnabled = new THREE.Uniform(params.pathEnabled ? 1.0 : 0.0)
    vU.uPathStart = new THREE.Uniform(new THREE.Vector3(0, -7, 0))
    vU.uPathEnd = new THREE.Uniform(new THREE.Vector3(0, 7, 0))
    vU.uPathAttraction = new THREE.Uniform(params.pathAttraction ?? 0.8)
    vU.uPathDriftSpeed = new THREE.Uniform(params.pathDriftSpeed ?? 3)
    vU.uPathDriftRate = new THREE.Uniform(params.pathDriftRate ?? 2)
    vU.uPathWaveFreq = new THREE.Uniform(params.pathWaveFreq ?? 0.7)
    vU.uPathWaveAmp = new THREE.Uniform(params.pathWaveAmp ?? 0.6)
    vU.uPathWaveSpeed = new THREE.Uniform(params.pathWaveSpeed ?? 1.0)
    vU.uPathSpiralFreq = new THREE.Uniform(params.pathSpiralFreq ?? 0.8)
    vU.uPathRadius = new THREE.Uniform(params.pathRadius ?? 1.4)
    vU.uPathSpread = new THREE.Uniform(11)
    vU.uTextBox0 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    vU.uTextBox0Strength = new THREE.Uniform(0)
    vU.uTextBox1 = new THREE.Uniform(new THREE.Vector4(0, 0, 0, 0))
    vU.uTextBox1Strength = new THREE.Uniform(0)
    vU.uTextTips0 = new THREE.Uniform(new THREE.Vector2(0, 0))
    vU.uTextTips1 = new THREE.Uniform(new THREE.Vector2(0, 0))
    vU.uTextDodge = new THREE.Uniform(params.textDodge ? 1.0 : 0.0)
    vU.uDomainHalfW = new THREE.Uniform(16)
    vU.uForceGradEnabled = new THREE.Uniform(params.forceGradEnabled ? 1.0 : 0.0)
    vU.uForceGradFloor = new THREE.Uniform(params.forceGradFloor ?? 0.35)
    vU.uForceGradVertical = new THREE.Uniform(params.forceGradVertical ?? 1.0)
    vU.uForceGradHorizontal = new THREE.Uniform(params.forceGradHorizontal ?? 1.0)
    vU.uForceGradPower = new THREE.Uniform(params.forceGradPower ?? 1.0)
    vU.uSymmetricField = new THREE.Uniform(params.symmetricField ? 1.0 : 0.0)
    vU.uProfileEnabled = new THREE.Uniform(0)
    vU.uProfileTex = new THREE.Uniform(profileDefaultTex)
    vU.uProfileYMin = new THREE.Uniform(0)
    vU.uProfileYMax = new THREE.Uniform(1)

    compute.computation.init()

    return { compute, baseParticlesTexture }
}
